/**
 * Smart Filters Loader - JSON ENDPOINT OPTIMERET VERSION
 * 
 * STRATEGI:
 * 1. Fetch ALLE produkter via custom JSON endpoint (250 per request!)
 * 2. Byg filtre med produktantal fra ALLE produkter
 * 3. Client-side show/hide filtrering (ingen page reload)
 * 
 * OPTIMERET FOR:
 * - Collections med 100-5000 produkter
 * - JSON endpoint: 250 produkter per request (vs 50 med HTML parsing)
 * - Minimal memory footprint - kun JSON data, ingen HTML parsing
 * - Instant filtrering uden reload
 * 
 * ENDPOINT: /collections/{handle}?view=smart-filters.json&page=N
 */

(function() {
  'use strict';
  
  class FilterLoader {
    constructor(container) {
      this.container = container;
      this.collectionHandle = container.getAttribute('data-collection-handle');
      this.excludedFields = (container.getAttribute('data-excluded-metafields') || '').split(',').map(s => s.trim());
      this.allProducts = []; // ALLE produkter i collection
      this.filterData = new Map();
      this.productCardCache = new Map(); // Cache for produktkort HTML
      
      const form = document.getElementById('smart-filters-form');
      this.replacements = [];
      if (form) {
        const replacementsData = form.getAttribute('data-replacements');
        if (replacementsData) {
          const lines = replacementsData.split('\n');
          lines.forEach(line => {
            const parts = line.trim().split('|');
            if (parts.length === 2) {
              this.replacements.push({ from: parts[0], to: parts[1] });
            }
          });
        }
      }
    }

    async init() {
      try {
        // VIS LOADING INDIKATOR
        this.showLoadingState();
        
        // Gem original pagination type for senere gendannelse
        this.savePaginationType();
        
        // Byg INSTANT filtre fra synlige produkter
        this.buildInitialFiltersFromPage();
        this.renderFilters();
        this.attachFilterChangeListeners();
        
        // Gendan filtre fra URL FØRST (før fetch af alle produkter)
        // Så brugeren ser filteret aktivt med det samme
        this.restoreFiltersFromURL();
        
        // Start fetch af ALLE produkter i baggrunden
        await this.fetchAllProducts();
        
        // Opdater filtre med ALLE værdier og korrekte counts
        this.updateFiltersWithAllProducts();
        
        // Hvis der ikke var nogen produkter på siden initialt, render filtrene nu
        if (this.container.querySelectorAll('.smart-filter-item[data-filter]').length === 0) {
          // console.log('🎨 Ingen filtre fundet i DOM - renderer alle filtre nu');
          this.renderFilters();
          // Tilføj event listeners EFTER filtrene er renderet
          this.attachFilterChangeListeners();
        }
        
        // Fjern loading state EFTER filtre er renderet
        this.hideLoadingState();
        
        // Gendan filtre fra URL IGEN efter alle produkter er loaded
        // for at sikre at alle matchende produkter vises
        this.restoreFiltersFromURL();
        
      } catch (error) {
        console.error('❌ Fejl ved indlæsning af filtre:', error);
        this.showError();
      }
    }
    
    showLoadingState() {
      // Vis loading badge på filter sektion
      const badge = document.createElement('div');
      badge.id = 'filter-loading-badge';
      badge.className = 'filter-loading-badge';
      badge.innerHTML = '<span class="spinner"></span> <span class="loading-text">Indlæser filtre...</span>';
      badge.style.cssText = 'position: absolute; top: 10px; right: 10px; background: #000; color: #fff; padding: 8px 12px; border-radius: 4px; font-size: 12px; display: flex; align-items: center; gap: 8px; z-index: 1000;';
      
      const spinner = badge.querySelector('.spinner');
      spinner.style.cssText = 'width: 12px; height: 12px; border: 2px solid #fff; border-top-color: transparent; border-radius: 50%; animation: spin 0.8s linear infinite;';
      
      // Tilføj CSS animation
      if (!document.getElementById('filter-loading-styles')) {
        const style = document.createElement('style');
        style.id = 'filter-loading-styles';
        style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
        document.head.appendChild(style);
      }
      
      this.container.style.position = 'relative';
      this.container.appendChild(badge);
    }
    
    hideLoadingState() {
      const badge = document.getElementById('filter-loading-badge');
      if (badge) badge.remove();
    }
    
    showFilteringLoader() {
      // Vis tekst i bunden af product grid
      const productGrid = document.getElementById('ProductsList');
      if (!productGrid) return;
      
      // Fjern eksisterende loader hvis den findes
      this.hideFilteringLoader();
      
      const loader = document.createElement('div');
      loader.id = 'filtering-loader';
      loader.style.cssText = `
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 1rem 0;
        font-size: 0.9rem;
        color: #999;
        width: 100%;
        grid-column: 1 / -1;
      `;
      
      const spinner = document.createElement('div');
      spinner.className = 'loader-spinner';
      spinner.style.cssText = `
        width: 14px;
        height: 14px;
        border: 2px solid #ddd;
        border-top-color: #666;
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
      `;
      
      const text = document.createElement('span');
      text.textContent = 'Indlæser produkter...';
      
      loader.appendChild(spinner);
      loader.appendChild(text);
      
      // Indsæt i BUNDEN af product grid
      productGrid.appendChild(loader);
    }
    
    hideFilteringLoader() {
      const loader = document.getElementById('filtering-loader');
      if (loader) {
        loader.style.opacity = '0';
        setTimeout(() => loader.remove(), 200);
      }
    }
    
    showProductInjectionLoader(totalProducts) {
      // Vis loading overlay specifikt for produkt injection
      const productGrid = document.getElementById('ProductsList');
      if (!productGrid) return;
      
      // Fjern eksisterende loader hvis den findes
      this.hideProductInjectionLoader();
      
      const loader = document.createElement('div');
      loader.id = 'product-injection-loader';
      loader.className = 'product-injection-loader';
      loader.innerHTML = `
        <div class="loader-content">
          <div class="loader-spinner"></div>
          <div class="loader-text">Indlæser <span class="loader-count">0</span> af ${totalProducts} matchende produkter...</div>
        </div>
      `;
      
      loader.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.95);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        opacity: 1;
        transition: opacity 0.3s ease;
      `;
      
      const content = loader.querySelector('.loader-content');
      content.style.cssText = `
        text-align: center;
        padding: 2rem;
      `;
      
      const spinner = loader.querySelector('.loader-spinner');
      spinner.style.cssText = `
        width: 48px;
        height: 48px;
        border: 4px solid #e5e5e5;
        border-top-color: #000;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
        margin: 0 auto 1rem;
      `;
      
      const text = loader.querySelector('.loader-text');
      text.style.cssText = `
        font-size: 1.1rem;
        color: #333;
        font-weight: 500;
      `;
      
      document.body.appendChild(loader);
    }
    
    updateProductInjectionProgress(current, total) {
      const loader = document.getElementById('product-injection-loader');
      if (!loader) return;
      
      const countSpan = loader.querySelector('.loader-count');
      if (countSpan) {
        countSpan.textContent = current;
      }
    }
    
    hideProductInjectionLoader() {
      const loader = document.getElementById('product-injection-loader');
      if (loader) {
        loader.style.opacity = '0';
        setTimeout(() => loader.remove(), 300);
      }
    }
    
    buildInitialFiltersFromPage() {
      // Byg filtre OG tilføj produkter fra synlige produkter (instant UI)
      const productCards = document.querySelectorAll('[data-product-metafields]');
      
      // console.log(`🔍 buildInitialFiltersFromPage: Fandt ${productCards.length} produktkort på siden`);
      
      // Brug et Map til at undgå duplikater
      const productMap = new Map();
      
      productCards.forEach(card => {
        try {
          const metafieldsAttr = card.getAttribute('data-product-metafields');
          const metafieldsData = JSON.parse(metafieldsAttr || '{}');
          const id = String(card.getAttribute('data-product-id')); // VIGTIGT: Konverter til string!
          const title = card.getAttribute('data-product-title') || '';
          const price = card.getAttribute('data-product-price') || '';
          
          // Hent URL og billede fra DOM
          const linkEl = card.querySelector('a[href*="/products/"]');
          const url = linkEl ? linkEl.getAttribute('href') : '';
          const imgEl = card.querySelector('img');
          const image = imgEl ? imgEl.getAttribute('src') : '';
          
          // Tilføj produkt til Map (undgår duplikater)
          // Konverter metafieldsData til object format (key: value)
          const metafields = {};
          Object.entries(metafieldsData).forEach(([key, metafieldObj]) => {
            let value = metafieldObj;
            if (metafieldObj && typeof metafieldObj === 'object' && 'value' in metafieldObj) {
              value = metafieldObj.value;
            }
            metafields[key] = value;
          });
          
          productMap.set(id, { id, title, price, url, image, metafields });
          
          // Byg også filterData
          Object.entries(metafieldsData).forEach(([key, metafieldObj]) => {
            let value = metafieldObj;
            if (metafieldObj && typeof metafieldObj === 'object' && 'value' in metafieldObj) {
              value = metafieldObj.value;
            }
            
            if (this.shouldSkipMetafield(key)) return;
            if (!value || value === '' || value === null) return;
            
            // Normalisér værdi
            value = String(value).trim();
            value = value.replace(/\s*(mm\.?|watt|ampere|volt)$/gi, '').trim();

            if (!this.filterData.has(key)) {
              this.filterData.set(key, {
                label: this.formatLabel(key),
                values: new Map()
              });
            }

            const filter = this.filterData.get(key);
            const currentCount = filter.values.get(value) || 0;
            filter.values.set(value, currentCount + 1);
          });
        } catch (err) {
          // Skip
        }
      });
      
      // Konverter Map til array
      this.allProducts = Array.from(productMap.values());
      
      // console.log(`✅ buildInitialFiltersFromPage: Tilføjede ${this.allProducts.length} produkter til allProducts`);
    }
    
    updateFiltersWithAllProducts() {
      // Genbyg filterData fra ALLE produkter
      this.filterData.clear();
      this.buildFilterData();
      
      // Opdater UI for eksisterende filtre UDEN at ændre deres hidden state
      this.filterData.forEach((data, key) => {
        const filterItem = this.container.querySelector(`[data-filter="${key}"]`);
        
        if (filterItem) {
          // Filter eksisterer allerede - opdater counts og bevare hidden state
          const wasHidden = filterItem.classList.contains('smart-filter-hidden');
          const dropdown = filterItem.querySelector('.smart-filter-dropdown');
          
          if (dropdown) {
            const values = Array.from(data.values.entries()).sort((a, b) => {
              const [valA] = a;
              const [valB] = b;
              const aNum = parseFloat(valA);
              const bNum = parseFloat(valB);
              if (!isNaN(aNum) && !isNaN(bNum)) {
                return aNum - bNum;
              }
              return String(valA).localeCompare(String(valB), 'da');
            });
            
            // Rebuild dropdown med nye værdier
            const valuesHtml = values.map(([value, count]) => {
              let displayValue = this.applyTextReplacements(String(value));
              
              if (key.toLowerCase().includes('watt')) {
                const numMatch = displayValue.match(/^(\d+(?:[.,]\d+)?)\s*$/);
                if (numMatch) {
                  displayValue = numMatch[1] + ' Watt';
                }
              }
              
              return `
                <label>
                  <input type="checkbox" 
                         name="sf_${key}" 
                         value="${this.escapeHtml(value)}">
                  <span>${this.escapeHtml(displayValue)}</span>
                </label>
              `;
            }).join('');
            
            dropdown.innerHTML = valuesHtml;
            
            // Gendan event listeners med dropdown-luk funktionalitet
            dropdown.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
              checkbox.addEventListener('change', () => {
                // Luk dropdown når man vælger en checkbox
                const dropdown = checkbox.closest('.smart-filter-dropdown');
                if (dropdown) {
                  dropdown.style.display = 'none';
                  const toggle = dropdown.previousElementSibling;
                  const icon = toggle?.querySelector('.toggle-icon');
                  if (icon) icon.textContent = '▼';
                }
                
                this.applyFilters();
                this.updateURL();
              });
            });
            
            // Bevar hidden state hvis filteret var skjult
            if (wasHidden && filterItem.style.display === 'none') {
              filterItem.style.display = 'none';
            }
          }
        }
        // SKIP tilføjelse af NYE filtre - vi vil kun opdatere eksisterende
      });
      
      // Opdater "Skjul X filtre" knap
      const showMoreBtn = this.container.querySelector('.smart-filter-show-more');
      if (showMoreBtn) {
        const hiddenItems = this.container.querySelectorAll('.smart-filter-hidden');
        const isExpanded = showMoreBtn.classList.contains('expanded');
        const hiddenCount = hiddenItems.length;
        
        if (hiddenCount > 0) {
          showMoreBtn.textContent = isExpanded 
            ? `- Skjul filtre` 
            : `+ ${hiddenCount} filtre`;
        }
      }
    }
    
    updateLoadingProgress(current, total) {
      const badge = document.getElementById('filter-loading-badge');
      if (!badge) return;
      
      const loadingText = badge.querySelector('.loading-text');
      if (loadingText) {
        const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
        loadingText.textContent = `Indlæser produkter: ${current} af ${total} (${percentage}%)`;
      }
    }
    
    async fetchAllProducts() {
      // Prøv JSON endpoint først (hurtigere), fald tilbage til AJAX
      const startTime = performance.now();
      
      try {
        const jsonProducts = await this.fetchProductsJSON();
        if (jsonProducts && jsonProducts.length > 0) {
          // Merge med eksisterende produkter
          const productMap = new Map();
          this.allProducts.forEach(p => productMap.set(p.id, p));
          jsonProducts.forEach(p => productMap.set(p.id, p));
          this.allProducts = Array.from(productMap.values());
          
          const endTime = performance.now();
          // console.log(`✅ JSON fetch færdig: ${this.allProducts.length} produkter på ${Math.round(endTime - startTime)}ms`);
          return;
        }
      } catch (e) {
        // console.log('⚠️ JSON endpoint ikke tilgængeligt, bruger AJAX...');
      }
      
      // Fallback til parallel AJAX
      await this.fetchAllProductsAjax();
    }
    
    async fetchProductsJSON() {
      // console.log('🚀 Prøver JSON endpoint...');
      const startTime = performance.now();
      
      // Start med TOM map - merger med allProducts sker i fetchAllProducts()
      const productMap = new Map();
      
      let page = 1;
      let hasMore = true;
      const BATCH_SIZE = 5; // 5 sider = 1250 produkter per batch
      
      while (hasMore && page <= 50) {
        const pagesToFetch = [];
        for (let i = 0; i < BATCH_SIZE && (page + i) <= 50; i++) {
          pagesToFetch.push(page + i);
        }
        
        const results = await Promise.all(
          pagesToFetch.map(p => this.fetchJSONPage(p))
        );
        
        let foundProducts = false;
        let gotValidResponse = false;
        
        for (const data of results) {
          // Hvis FØRSTE side returnerer null, så virker endpoint ikke
          if (!data && !gotValidResponse) {
            throw new Error('JSON endpoint ikke tilgængeligt');
          }
          
          // Skip null responses (tomme sider)
          if (!data) {
            hasMore = false;
            continue;
          }
          
          gotValidResponse = true;
          
          if (data.products && data.products.length > 0) {
            foundProducts = true;
            data.products.forEach(p => {
              // VIGTIGT: Konverter ID til string for konsistens
              p.id = String(p.id);
              
              // VIGTIGT: Konverter pris til string (ØRE format)
              // JSON kan returnere pris som number eller string, måske i kroner
              if (p.price) {
                const priceNum = parseFloat(p.price);
                // Hvis pris er under 1000, antag det er i kroner og konverter til øre
                if (priceNum < 1000) {
                  p.price = String(Math.round(priceNum * 100));
                } else {
                  p.price = String(Math.round(priceNum));
                }
              }
              
              // metafields er allerede et object fra JSON endpoint
              // Ingen konvertering nødvendig - det er klar til brug
              if (!p.metafields || typeof p.metafields !== 'object') {
                p.metafields = {};
              }
              
              productMap.set(p.id, p);
            });
          }
          
          if (data.pagination && !data.pagination.has_next) {
            hasMore = false;
            break;
          }
        }
        
        if (!foundProducts) hasMore = false;
        
        // Opdater progress
        const form = document.getElementById('smart-filters-form');
        const totalProducts = form ? parseInt(form.getAttribute('data-total-products')) || productMap.size : productMap.size;
        this.updateLoadingProgress(productMap.size, totalProducts);
        
        page += BATCH_SIZE;
      }
      
      // console.log(`📦 JSON: ${productMap.size} produkter på ${Math.round(performance.now() - startTime)}ms`);
      return Array.from(productMap.values());
    }
    
    async fetchJSONPage(pageNum) {
      try {
        // View navn matcher template: collection.smart-filters.json.liquid
        const url = `/collections/${this.collectionHandle}?view=smart-filters.json&page=${pageNum}`;
        const response = await fetch(url);
        
        if (!response.ok) {
          // console.warn(`❌ JSON side ${pageNum}: HTTP ${response.status}`);
          return null;
        }
        
        const text = await response.text();
        
        // Check om det er JSON (ikke HTML)
        if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
          try {
            const data = JSON.parse(text);
            // console.log(`✅ JSON side ${pageNum}: ${data.products?.length || 0} produkter`);
            return data;
          } catch (parseError) {
            // console.warn(`❌ JSON side ${pageNum}: Ugyldig JSON`);
            // Returner tom side (ikke null) for at undgå at stoppe hele fetch
            return { products: [], pagination: { has_next: false } };
          }
        }
        
        // console.warn(`❌ JSON side ${pageNum}: Fik HTML i stedet for JSON`);
        return null;
      } catch (error) {
        // console.warn(`❌ JSON side ${pageNum} fejl:`, error.message);
        return null;
      }
    }
    
    async fetchAllProductsAjax() {
      // Parallel AJAX fetch - 20 sider ad gangen for hurtig indlæsning
      // console.log('🚀 Starter AJAX fetch af ALLE produkter...');
      const startTime = performance.now();
      
      const productMap = new Map();
      
      // Tilføj eksisterende produkter fra siden til Map
      this.allProducts.forEach(p => productMap.set(p.id, p));
      
      // Parallel AJAX fetch - HENT ALLE SIDER så vi har ALT cached
      const BATCH_SIZE = 20;
      let page = 1;
      let hasMore = true;
      let productsPerPage = null;
      
      // FØRST: Find ud af hvor mange sider der er
      const firstPageResponse = await fetch(`/collections/${this.collectionHandle}?page=1`);
      const firstPageHtml = await firstPageResponse.text();
      
      // Parse for at finde sidste side nummer
      const parser = new DOMParser();
      const doc = parser.parseFromString(firstPageHtml, 'text/html');
      const paginationLinks = doc.querySelectorAll('.pagination a');
      let maxPage = 1;
      paginationLinks.forEach(link => {
        const pageMatch = link.getAttribute('href')?.match(/[?&]page=(\d+)/);
        if (pageMatch) {
          const pageNum = parseInt(pageMatch[1]);
          if (pageNum > maxPage) maxPage = pageNum;
        }
      });
      
      // console.log(`📄 Collection har ${maxPage} sider`);
      
      // NU: Fetch ALLE sider parallelt for at cache ALT
      while (hasMore && page <= maxPage) {
        const pagesToFetch = [];
        for (let i = 0; i < BATCH_SIZE && (page + i) <= maxPage; i++) {
          pagesToFetch.push(page + i);
        }
        
        const results = await Promise.all(
          pagesToFetch.map(p => this.fetchPageAjax(p))
        );
        
        let foundProducts = false;
        let batchCount = 0;
        
        for (const products of results) {
          if (products.length > 0) {
            foundProducts = true;
            batchCount += products.length;
            if (!productsPerPage) productsPerPage = products.length;
            products.forEach(p => productMap.set(p.id, p));
          }
        }
        
        // SKIP batch logging - viser kun SLUT total for klarhed
        
        page += BATCH_SIZE;
      }
      
      // Konverter Map til array
      this.allProducts = Array.from(productMap.values());
      
      const endTime = performance.now();
      // console.log(`✅ AJAX fetch færdig: ${this.allProducts.length} unikke produkter, ${this.productCardCache.size} HTML cached på ${Math.round(endTime - startTime)}ms`);
    }
    
    async fetchPageAjax(pageNum) {
      try {
        const url = `/collections/${this.collectionHandle}?page=${pageNum}`;
        const response = await fetch(url);
        
        if (!response.ok) return [];
        
        const html = await response.text();
        return this.extractProductsFromHTML(html);
      } catch (error) {
        // console.warn(`Fejl ved hentning af side ${pageNum}:`, error);
        return [];
      }
    }
    
    extractProductsFromHTML(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const productCards = doc.querySelectorAll('[data-product-metafields]');
      const products = [];
      
      productCards.forEach(card => {
        try {
          const metafieldsAttr = card.getAttribute('data-product-metafields');
          const metafieldsData = JSON.parse(metafieldsAttr || '{}');
          const id = String(card.getAttribute('data-product-id'));
          const title = card.getAttribute('data-product-title') || '';
          const price = card.getAttribute('data-product-price') || '0';
          
          // Hent URL og billede fra DOM
          const linkEl = card.querySelector('a[href*="/products/"]');
          const url = linkEl ? linkEl.getAttribute('href') : '';
          const imgEl = card.querySelector('img');
          const image = imgEl ? imgEl.getAttribute('src') : '';
          
          const metafields = [];
          Object.entries(metafieldsData).forEach(([key, metafieldObj]) => {
            let value = metafieldObj;
            if (metafieldObj && typeof metafieldObj === 'object' && 'value' in metafieldObj) {
              value = metafieldObj.value;
            }
            metafields.push({ key, value });
          });
          
          products.push({ id, title, price, url, image, metafields });
          
          // Cache produktkort HTML
          const wrapper = card.closest('.f-column.product-column');
          if (wrapper) {
            this.productCardCache.set(id, wrapper.outerHTML);
          } else {
            this.productCardCache.set(id, `<div class="f-column product-column">${card.outerHTML}</div>`);
          }
        } catch (err) {
          // Skip
        }
      });
      
      return products;
    }
    
    buildFilterData() {
      // console.log('🔨 buildFilterData: Starting med', this.allProducts.length, 'produkter');
      
      this.allProducts.forEach(product => {
        // metafields er nu et object, ikke et array
        if (product.metafields && typeof product.metafields === 'object') {
          // console.log('📦 Produkt metafields:', product.title, product.metafields);
          
          Object.entries(product.metafields).forEach(([key, value]) => {
            if (this.shouldSkipMetafield(key)) return;
            if (!value || value === '' || value === null) return;
            
            // Normalisér værdi: fjern units (mm, Watt, etc.)
            value = String(value).trim();
            value = value.replace(/\s*(mm\.?|watt|ampere|volt)$/gi, '').trim();

            if (!this.filterData.has(key)) {
              this.filterData.set(key, {
                label: this.formatLabel(key),
                values: new Map() // Map<value, count>
              });
            }

            const filter = this.filterData.get(key);
            const currentCount = filter.values.get(value) || 0;
            filter.values.set(value, currentCount + 1);
          });
        }
      });
      
      // console.log('✅ buildFilterData: Byggede', this.filterData.size, 'filtre:', Array.from(this.filterData.keys()));
    }
    
    restoreFiltersFromURL() {
      const urlParams = new URLSearchParams(window.location.search);
      let restoredCount = 0;
      
      urlParams.forEach((value, key) => {
        if (key.startsWith('sf_')) {
          const metafieldKey = key.replace('sf_', '');
          const decodedValue = decodeURIComponent(value.replace(/\+/g, ' '));
          
          const checkbox = this.container.querySelector(
            `input[name="sf_${metafieldKey}"][value="${decodedValue}"]`
          );
          
          if (checkbox) {
            checkbox.checked = true;
            restoredCount++;
          }
        }
        
        // Pris filters
        if (key === 'sf_price_min') {
          const input = document.querySelector('[name="sf_price_min"]');
          if (input) input.value = value;
        }
        if (key === 'sf_price_max') {
          const input = document.querySelector('[name="sf_price_max"]');
          if (input) input.value = value;
        }
      });
      
      if (restoredCount > 0) {
        this.applyFilters();
      }
    }
    
    attachFilterChangeListeners() {
      const form = document.getElementById('smart-filters-form');
      
      // Odoo metafield checkboxes
      const checkboxes = this.container.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          // Luk dropdown når man vælger en checkbox
          const dropdown = checkbox.closest('.smart-filter-dropdown');
          if (dropdown) {
            dropdown.style.display = 'none';
            const toggle = dropdown.previousElementSibling;
            const icon = toggle?.querySelector('.toggle-icon');
            if (icon) icon.textContent = '▼';
          }
          
          this.applyFilters();
          this.updateURL();
        });
      });
      
      // Pris inputs og sliders med synkronisering
      if (form) {
        const priceMinInput = form.querySelector('[name="sf_price_min"]');
        const priceMaxInput = form.querySelector('[name="sf_price_max"]');
        const priceRangeMin = document.getElementById('filter-price-range-min');
        const priceRangeMax = document.getElementById('filter-price-range-max');
        const priceProgress = document.getElementById('price-range-progress');
        
        let priceTimeout;
        
        // Synkroniser slider position ved init
        const updateSliderVisual = () => {
          if (!priceRangeMin || !priceRangeMax || !priceProgress) return;
          
          const min = parseFloat(priceRangeMin.min);
          const max = parseFloat(priceRangeMax.max);
          const valMin = parseFloat(priceRangeMin.value);
          const valMax = parseFloat(priceRangeMax.value);
          
          const percentMin = ((valMin - min) / (max - min)) * 100;
          const percentMax = ((valMax - min) / (max - min)) * 100;
          
          priceProgress.style.left = percentMin + '%';
          priceProgress.style.width = (percentMax - percentMin) + '%';
        };
        
        // Number inputs → sync til sliders og filtrer
        if (priceMinInput) {
          priceMinInput.addEventListener('input', () => {
            if (priceRangeMin) priceRangeMin.value = priceMinInput.value;
            updateSliderVisual();
            clearTimeout(priceTimeout);
            priceTimeout = setTimeout(() => {
              this.applyFilters();
              this.updateURL();
            }, 500);
          });
        }
        
        if (priceMaxInput) {
          priceMaxInput.addEventListener('input', () => {
            if (priceRangeMax) priceRangeMax.value = priceMaxInput.value;
            updateSliderVisual();
            clearTimeout(priceTimeout);
            priceTimeout = setTimeout(() => {
              this.applyFilters();
              this.updateURL();
            }, 500);
          });
        }
        
        // Range sliders → sync til inputs og filtrer
        if (priceRangeMin) {
          priceRangeMin.addEventListener('input', () => {
            const minVal = parseFloat(priceRangeMin.value);
            const maxVal = parseFloat(priceRangeMax.value);
            
            // Prevent crossing
            if (minVal >= maxVal) {
              priceRangeMin.value = maxVal - 1;
            }
            
            if (priceMinInput) priceMinInput.value = priceRangeMin.value;
            updateSliderVisual();
            clearTimeout(priceTimeout);
            priceTimeout = setTimeout(() => {
              this.applyFilters();
              this.updateURL();
            }, 500);
          });
        }
        
        if (priceRangeMax) {
          priceRangeMax.addEventListener('input', () => {
            const minVal = parseFloat(priceRangeMin.value);
            const maxVal = parseFloat(priceRangeMax.value);
            
            // Prevent crossing
            if (maxVal <= minVal) {
              priceRangeMax.value = minVal + 1;
            }
            
            if (priceMaxInput) priceMaxInput.value = priceRangeMax.value;
            updateSliderVisual();
            clearTimeout(priceTimeout);
            priceTimeout = setTimeout(() => {
              this.applyFilters();
              this.updateURL();
            }, 500);
          });
        }
        
        // Initial slider position
        updateSliderVisual();
      }
    }
    
    updateURL() {
      const url = new URL(window.location);
      
      // Ryd alle sf_ parametre
      Array.from(url.searchParams.keys()).forEach(key => {
        if (key.startsWith('sf_')) {
          url.searchParams.delete(key);
        }
      });
      
      // Tilføj pris filter
      const form = document.getElementById('smart-filters-form');
      if (form) {
        const priceMin = form.querySelector('[name="sf_price_min"]')?.value;
        const priceMax = form.querySelector('[name="sf_price_max"]')?.value;
        const originalMin = form.getAttribute('data-min-price');
        const originalMax = form.getAttribute('data-max-price');
        
        if (priceMin && priceMin !== originalMin) {
          url.searchParams.set('sf_price_min', priceMin);
        }
        if (priceMax && priceMax !== originalMax) {
          url.searchParams.set('sf_price_max', priceMax);
        }
      }
      
      // Tilføj odoo metafield filters
      const checkboxes = this.container.querySelectorAll('input[type="checkbox"]:checked');
      checkboxes.forEach(checkbox => {
        if (checkbox.name.startsWith('sf_')) {
          url.searchParams.append(checkbox.name, checkbox.value);
        }
      });
      
      // Opdater URL uden reload
      window.history.pushState({}, '', url);
    }
    
    applyFilters() {
      // Vis loading animation
      this.showFilteringLoader();
      
      // Brug setTimeout 0 for hurtigere execution
      setTimeout(() => {
        this.performFiltering();
      }, 0);
    }
    
    performFiltering() {
      // Saml valgte filtre
      const selectedFilters = new Map();
      let priceMin = null;
      let priceMax = null;
      let originalMin = null;
      let originalMax = null;
      let hasPriceFilter = false;
      let debugLogged = false;
      
      const form = document.getElementById('smart-filters-form');
      if (form) {
        const priceMinInput = form.querySelector('[name="sf_price_min"]');
        const priceMaxInput = form.querySelector('[name="sf_price_max"]');
        originalMin = parseFloat(form.getAttribute('data-min-price')) || 0;
        originalMax = parseFloat(form.getAttribute('data-max-price')) || 999999;
        
        if (priceMinInput && priceMaxInput) {
          priceMin = parseFloat(priceMinInput.value);
          priceMax = parseFloat(priceMaxInput.value);
          
          // Kun aktiv prisfilter hvis værdierne er ændret fra original
          hasPriceFilter = (priceMin !== originalMin || priceMax !== originalMax);
        }
      }
      
      const checkboxes = this.container.querySelectorAll('input[type="checkbox"]:checked');
      checkboxes.forEach(checkbox => {
        if (checkbox.name.startsWith('sf_')) {
          const key = checkbox.name.replace('sf_', '');
          if (!selectedFilters.has(key)) {
            selectedFilters.set(key, new Set());
          }
          selectedFilters.get(key).add(checkbox.value);
        }
      });
      
      // DEBUG: Vis hvad der er valgt
      if (selectedFilters.size > 0) {
        // console.log('🔍 Valgte filtre:', Array.from(selectedFilters.entries()).map(([k, v]) => `${k}: ${Array.from(v).join(', ')}`).join(' | '));
      }
      
      // Hvis ingen filtre er aktive, vis alle
      if (selectedFilters.size === 0 && !hasPriceFilter) {
        this.showAllProducts();
        this.updateAvailableFilterValues(new Set(this.allProducts.map(p => String(p.id))));
        return;
      }
      
      // Filtrer produkter
      const visibleProductIds = new Set();
      
      // Kun log hvis der er valgte filtre (reducerer spam)
      const hasFilters = selectedFilters.size > 0 || hasPriceFilter;
      
      this.allProducts.forEach((product, index) => {
        let matches = true;
        
        // Check metafield filtre
        for (const [filterKey, filterValues] of selectedFilters.entries()) {
          // metafields er nu et object, ikke et array
          const productValue = product.metafields?.[filterKey];
          
          if (!productValue) {
            matches = false;
            break;
          }
          
          // Normalisér product værdi: fjern units
          let normalizedProductValue = String(productValue).trim();
          normalizedProductValue = normalizedProductValue.replace(/\s*(mm\.?|watt|ampere|volt)$/gi, '').trim();
          
          // Check både exact match og normaliseret match (komma → punktum)
          let found = false;
          for (const filterValue of filterValues) {
            const normalizedFilterValue = String(filterValue).trim();
            if (normalizedProductValue === normalizedFilterValue || 
                normalizedProductValue === normalizedFilterValue.replace(',', '.') ||
                normalizedProductValue.replace(',', '.') === normalizedFilterValue.replace(',', '.')) {
              found = true;
              break;
            }
          }
          
          if (!found) {
            matches = false;
            break;
          }
        }
        
        // Check pris filter - KONVERTER TIL SAMME ENHED!
        // product.price er i ØRE (string), men priceMin/Max er i KRONER
        if (matches && hasPriceFilter) {
          const productPriceInOre = parseFloat(product.price) || 0;
          const productPriceInKr = productPriceInOre / 100; // Konverter øre → kr
          
          if (isNaN(productPriceInKr) || productPriceInKr < priceMin || productPriceInKr > priceMax) {
            matches = false;
          }
        }
        
        if (matches) {
          visibleProductIds.add(String(product.id)); // Sikr string
        }
      });
      
      // Log kun hvis der faktisk er filtrering
      if (hasFilters) {
        // console.log(`✅ ${visibleProductIds.size} af ${this.allProducts.length} produkter matcher`);
      }
      
      this.updateProductVisibility(visibleProductIds);
      this.updateAvailableFilterValues(visibleProductIds);
      this.updateActiveFiltersDisplay(selectedFilters, hasPriceFilter, priceMin, priceMax);
    }
    
    updateActiveFiltersDisplay(selectedFilters, hasPriceFilter, priceMin, priceMax) {
      // Opdater visning af aktive filtre
      const activeFiltersContainer = document.getElementById('active-filters');
      const activeFiltersList = activeFiltersContainer?.querySelector('.active-filters-list');
      
      if (!activeFiltersContainer || !activeFiltersList) return;
      
      // Ryd eksisterende tags
      activeFiltersList.innerHTML = '';
      
      let hasActiveFilters = false;
      
      // Tilføj metafield filter tags
      selectedFilters.forEach((values, key) => {
        values.forEach(value => {
          hasActiveFilters = true;
          const tag = document.createElement('span');
          tag.className = 'active-filter-tag';
          
          const filterData = this.filterData.get(key);
          const label = filterData ? filterData.label : this.formatLabel(key);
          let displayValue = this.applyTextReplacements(String(value));
          
          tag.innerHTML = `
            <span class="tag-label">${this.escapeHtml(label)}:</span>
            <span class="tag-value">${this.escapeHtml(displayValue)}</span>
            <button type="button" class="tag-remove" data-filter="${key}" data-value="${this.escapeHtml(value)}">×</button>
          `;
          activeFiltersList.appendChild(tag);
        });
      });
      
      // Tilføj pris filter tag
      if (hasPriceFilter) {
        hasActiveFilters = true;
        const tag = document.createElement('span');
        tag.className = 'active-filter-tag';
        tag.innerHTML = `
          <span class="tag-label">Pris:</span>
          <span class="tag-value">${priceMin} - ${priceMax} kr</span>
          <button type="button" class="tag-remove" data-filter="price">×</button>
        `;
        activeFiltersList.appendChild(tag);
      }
      
      // Vis/skjul container
      activeFiltersContainer.style.display = hasActiveFilters ? 'block' : 'none';
      
      // Attach event listeners til remove knapper
      activeFiltersList.querySelectorAll('.tag-remove').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const filterKey = e.target.getAttribute('data-filter');
          const filterValue = e.target.getAttribute('data-value');
          
          if (filterKey === 'price') {
            // Reset pris filter
            const form = document.getElementById('smart-filters-form');
            if (form) {
              const minInput = form.querySelector('[name="sf_price_min"]');
              const maxInput = form.querySelector('[name="sf_price_max"]');
              const originalMin = form.getAttribute('data-min-price');
              const originalMax = form.getAttribute('data-max-price');
              if (minInput) minInput.value = originalMin;
              if (maxInput) maxInput.value = originalMax;
              
              // Opdater sliders
              const rangeMin = document.getElementById('filter-price-range-min');
              const rangeMax = document.getElementById('filter-price-range-max');
              if (rangeMin) rangeMin.value = originalMin;
              if (rangeMax) rangeMax.value = originalMax;
            }
          } else {
            // Uncheck den specifikke checkbox
            // VIGTIGT: filterValue kan være HTML-escaped, så unescape først
            const unescapedValue = this.unescapeHtml(filterValue);
            const checkbox = this.container.querySelector(
              `input[name="sf_${filterKey}"][value="${this.escapeHtml(unescapedValue)}"]`
            );
            if (checkbox) {
              checkbox.checked = false;
            }
          }
          
          this.applyFilters();
          this.updateURL();
        });
      });
      
      // Setup "Ryd alle" knap
      const clearAllBtn = activeFiltersContainer.querySelector('.clear-all-filters');
      if (clearAllBtn) {
        clearAllBtn.onclick = () => this.clearAllFilters();
      }
    }
    
    clearAllFilters() {
      // Ryd alle checkboxes
      this.container.querySelectorAll('input[type="checkbox"]:checked').forEach(cb => {
        cb.checked = false;
      });
      
      // Reset pris filter
      const form = document.getElementById('smart-filters-form');
      if (form) {
        const minInput = form.querySelector('[name="sf_price_min"]');
        const maxInput = form.querySelector('[name="sf_price_max"]');
        const originalMin = form.getAttribute('data-min-price');
        const originalMax = form.getAttribute('data-max-price');
        if (minInput) minInput.value = originalMin;
        if (maxInput) maxInput.value = originalMax;
        
        const rangeMin = document.getElementById('filter-price-range-min');
        const rangeMax = document.getElementById('filter-price-range-max');
        if (rangeMin) rangeMin.value = originalMin;
        if (rangeMax) rangeMax.value = originalMax;
      }
      
      // Skjul active filters display
      const activeFiltersContainer = document.getElementById('active-filters');
      if (activeFiltersContainer) {
        activeFiltersContainer.style.display = 'none';
      }
      
      // Ryd URL parametre
      const url = new URL(window.location);
      Array.from(url.searchParams.keys()).forEach(key => {
        if (key.startsWith('sf_')) url.searchParams.delete(key);
      });
      window.history.pushState({}, '', url);
      
      // VIS ALLE produkter - brug showAllProducts() metoden
      this.showAllProducts();
      
      // Opdater available filter values til at vise alle
      this.updateAvailableFilterValues(new Set(this.allProducts.map(p => String(p.id))));
      
      // Genaktiver pagination
      this.disablePagination(false);
    }
    
    updateAvailableFilterValues(visibleProductIds) {
      // Find alle produkter der matcher nuværende filtre
      const matchingProducts = this.allProducts.filter(p => visibleProductIds.has(String(p.id)));
      
      // Find hvilke filtre der har aktive valg
      const activeFilterKeys = new Set();
      this.container.querySelectorAll('input[type="checkbox"]:checked').forEach(checkbox => {
        if (checkbox.name.startsWith('sf_')) {
          activeFilterKeys.add(checkbox.name.replace('sf_', ''));
        }
      });
      
      // Byg map af tilgængelige værdier per filter
      const availableValues = new Map();
      
      matchingProducts.forEach(product => {
        // Håndter både object og array format for metafields
        if (product.metafields && typeof product.metafields === 'object') {
          if (Array.isArray(product.metafields)) {
            // Array format fra HTML parsing
            product.metafields.forEach(metafield => {
              const key = metafield.key;
              let value = metafield.value;
              
              if (this.shouldSkipMetafield(key)) return;
              if (!value || value === '' || value === null) return;
              
              // Normalisér værdi
              value = String(value).trim();
              value = value.replace(/\s*(mm\.?|watt|ampere|volt)$/gi, '').trim();
              
              if (!availableValues.has(key)) {
                availableValues.set(key, new Set());
              }
              availableValues.get(key).add(value);
            });
          } else {
            // Object format fra JSON endpoint
            Object.entries(product.metafields).forEach(([key, value]) => {
              if (this.shouldSkipMetafield(key)) return;
              if (!value || value === '' || value === null) return;
              
              // Normalisér værdi
              value = String(value).trim();
              value = value.replace(/\s*(mm\.?|watt|ampere|volt)$/gi, '').trim();
              
              if (!availableValues.has(key)) {
                availableValues.set(key, new Set());
              }
              availableValues.get(key).add(value);
            });
          }
        }
      });
      
      // Opdater UI - skjul/vis checkboxes baseret på tilgængelighed
      this.filterData.forEach((data, filterKey) => {
        const filterItem = this.container.querySelector(`[data-filter="${filterKey}"]`);
        if (!filterItem) return;
        
        const checkboxes = filterItem.querySelectorAll('input[type="checkbox"]');
        const availableForThisFilter = availableValues.get(filterKey) || new Set();
        
        // VIGTIGT: Hvis dette filter HAR aktive valg, vis ALLE værdier i filteret
        // så brugeren kan vælge flere værdier fra samme filter
        const filterHasActiveSelection = activeFilterKeys.has(filterKey);
        
        let hasVisibleOptions = false;
        
        checkboxes.forEach(checkbox => {
          const value = checkbox.value;
          const label = checkbox.closest('label');
          
          // Hvis checkboxen er checked, behold den altid synlig
          if (checkbox.checked) {
            label.style.display = '';
            label.style.opacity = '1';
            hasVisibleOptions = true;
            return;
          }
          
          // HVIS dette filter har aktive valg, vis ALLE værdier i filteret
          // så brugeren kan tilføje flere værdier (fx vælge både 300 og 400 Lumen)
          if (filterHasActiveSelection) {
            label.style.display = '';
            label.style.opacity = '1';
            hasVisibleOptions = true;
            return;
          }
          
          // Ellers skjul hvis værdien ikke er tilgængelig i matchende produkter
          if (availableForThisFilter.has(value)) {
            label.style.display = '';
            label.style.opacity = '1';
            hasVisibleOptions = true;
          } else {
            label.style.display = 'none';
          }
        });
        
        // Tæl antal synlige labels mere præcist
        let visibleCheckboxCount = 0;
        filterItem.querySelectorAll('label').forEach(label => {
          const isHidden = label.style.display === 'none';
          if (!isHidden) visibleCheckboxCount++;
        });
        
        // Skjul hele filteret hvis der ikke er synlige muligheder ELLER kun én mulighed eller 0 muligheder
        if (!hasVisibleOptions || visibleCheckboxCount === 0 || visibleCheckboxCount === 1) {
          filterItem.style.display = 'none';
        } else {
          filterItem.style.display = '';
        }
      });
      
      // OPDATER "Vis X flere filtre" knappen med korrekt antal SYNLIGE filtre
      this.updateShowMoreButtonCount();
    }
    
    updateShowMoreButtonCount() {
      const showMoreBtn = this.container.querySelector('.smart-filter-show-more');
      const hiddenContainer = this.container.querySelector('.smart-filters-hidden');
      
      if (!showMoreBtn || !hiddenContainer) return;
      
      // Tæl kun SYNLIGE filtre i hidden container
      const visibleHiddenFilters = hiddenContainer.querySelectorAll('.smart-filter-item:not([style*="display: none"])');
      const visibleCount = visibleHiddenFilters.length;
      
      const isExpanded = hiddenContainer.style.display !== 'none';
      
      if (visibleCount === 0) {
        // Skjul knappen hvis der ikke er nogen synlige filtre
        showMoreBtn.style.display = 'none';
      } else {
        showMoreBtn.style.display = '';
        showMoreBtn.textContent = isExpanded 
          ? `- Skjul ${visibleCount} filtre` 
          : `+ ${visibleCount} flere filtre`;
      }
    }
    
    showAllProducts() {
      const productCards = document.querySelectorAll('[data-product-card]');
      productCards.forEach(card => {
        card.style.cssText = '';
        const parentColumn = card.closest('.f-column.product-column');
        if (parentColumn) parentColumn.style.cssText = '';
      });
      this.updateProductCount(this.allProducts.length);
      this.hideMissingProductsWarning();
      this.hideNoProductsMessage();
      this.hideFilteringLoader();
    }
    
    async updateProductVisibility(visibleProductIds) {
      // VIGTIG: Vis KUN matchende produkter - inject dem der mangler fra cache
      
      const productGrid = document.getElementById('ProductsList');
      
      if (!productGrid) {
        console.error('❌ Kunne ikke finde #ProductsList!');
        return;
      }
      
      // Først: Find hvilke matchende produkter der IKKE er på siden endnu
      const existingProductIds = new Set();
      document.querySelectorAll('[data-product-card]').forEach(card => {
        existingProductIds.add(String(card.getAttribute('data-product-id')));
      });
      
      // Find KUN matchende produkter der mangler på siden
      const missingMatchingProducts = this.allProducts.filter(p => 
        visibleProductIds.has(String(p.id)) && !existingProductIds.has(String(p.id))
      );
      
      // console.log(`📊 Matchende produkter: ${visibleProductIds.size}`);
      // console.log(`📊 Allerede på siden: ${existingProductIds.size}`);
      // console.log(`📊 Mangler at injecte: ${missingMatchingProducts.length}`);
      
      // Inject KUN manglende matchende produkter - AWAIT fordi den nu er async
      if (missingMatchingProducts.length > 0) {
        await this.injectMissingProducts(missingMatchingProducts, productGrid);
      }
      
      // Nu skjul/vis ALLE produktkort på siden - INKLUSIV nyligt injected
      let shownCount = 0;
      let hiddenCount = 0;
      
      // Hent ALLE produktkort igen (inkl. nyligt injected)
      document.querySelectorAll('[data-product-card]').forEach(card => {
        const productId = String(card.getAttribute('data-product-id'));
        const parentColumn = card.closest('.f-column.product-column');
        
        // VIS KUN hvis produktet matcher filteret
        if (visibleProductIds.has(productId)) {
          card.style.cssText = '';
          if (parentColumn) parentColumn.style.cssText = '';
          shownCount++;
        } else {
          // SKJUL alle der IKKE matcher
          card.style.cssText = 'display: none !important;';
          if (parentColumn) parentColumn.style.cssText = 'display: none !important;';
          hiddenCount++;
        }
      });
      
      // console.log(`✅ Viste produkter: ${shownCount}, Skjulte: ${hiddenCount}`);
      
      this.updateProductCount(visibleProductIds.size);
      this.hideMissingProductsWarning();
      this.hideNoProductsMessage();
      
      // VIGTIGT: Skjul pagination/infinite scroll når filtre er aktive
      // Ellers loader den alle produkter selvom de ikke matcher filteret
      this.disablePagination(visibleProductIds.size < this.allProducts.length);
      
      // Skjul filtering loader når alt er færdigt
      this.hideFilteringLoader();
    }
    
    async injectMissingProducts(missingProducts, productGrid) {
      // Inject produkter - klon eksisterende produktkort og udskift data
      // console.log(`🔄 Injector ${missingProducts.length} manglende produkter...`);
      
      // Vis loading animation hvis vi skal injecte produkter
      if (missingProducts.length > 0) {
        this.showProductInjectionLoader(missingProducts.length);
      }
      
      // Find et template produktkort at klone fra
      const templateCard = document.querySelector('[data-product-card]');
      if (!templateCard) {
        console.error('❌ Kunne ikke finde template produktkort at klone fra');
        this.hideProductInjectionLoader();
        return;
      }
      
      const templateColumn = templateCard.closest('.f-column.product-column');
      if (!templateColumn) {
        console.error('❌ Kunne ikke finde template kolonne');
        this.hideProductInjectionLoader();
        return;
      }
      
      let injectedCount = 0;
      
      for (const product of missingProducts) {
        const productId = String(product.id);
        
        // Tjek om produktet allerede er på siden
        if (document.querySelector(`[data-product-card][data-product-id="${productId}"]`)) {
          continue;
        }
        
        // Klon template kolonnen
        const newColumn = templateColumn.cloneNode(true);
        const newCard = newColumn.querySelector('[data-product-card]');
        
        if (!newCard) continue;
        
        // Opdater product data attributes
        newCard.setAttribute('data-product-id', productId);
        newCard.setAttribute('data-variant-id', productId);
        newCard.setAttribute('data-product-title', product.title);
        newCard.setAttribute('data-product-price', product.price);
        newCard.setAttribute('data-variant-price', (product.price / 100).toFixed(2));
        
        // Opdater metafields attribute
        let metafieldsJson;
        if (Array.isArray(product.metafields)) {
          // Array format fra HTML parsing
          metafieldsJson = JSON.stringify(
            product.metafields.reduce((acc, mf) => {
              acc[mf.key] = { value: mf.value };
              return acc;
            }, {})
          );
        } else {
          // Object format fra JSON endpoint - allerede i korrekt format
          metafieldsJson = JSON.stringify(product.metafields);
        }
        newCard.setAttribute('data-product-metafields', metafieldsJson);
        
        // Opdater title og links
        const titleLink = newCard.querySelector('.product-card__title a');
        const imageLink = newCard.querySelector('.product-card__image-wrapper a');
        if (titleLink) {
          titleLink.setAttribute('href', product.url);
          const titleText = titleLink.querySelector('.reversed-link__text');
          if (titleText) titleText.textContent = product.title;
        }
        if (imageLink) {
          imageLink.setAttribute('href', product.url);
          imageLink.setAttribute('aria-label', product.title);
        }
        
        // Opdater billede
        const img = newCard.querySelector('img');
        if (img && product.image) {
          img.setAttribute('src', product.image);
          img.setAttribute('alt', product.title);
        }
        
        // Opdater pris
        const priceItem = newCard.querySelector('.price-item--regular');
        if (priceItem) {
          const priceInKr = product.price / 100;
          const priceFormatted = new Intl.NumberFormat('da-DK', { 
            style: 'currency', 
            currency: 'DKK',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(priceInKr);
          priceItem.textContent = priceFormatted;
        }
        
        // Inject i grid
        productGrid.appendChild(newColumn);
        injectedCount++;
        
        // Opdater progress i loading animation
        this.updateProductInjectionProgress(injectedCount, missingProducts.length);
      }
      
      // Skjul loading animation
      this.hideProductInjectionLoader();
      
      // console.log(`✅ Injected ${injectedCount} produkter ved at klone template`);
    }
    
    async fetchProductCardHTML(productId) {
      // STRATEGI: Søg gennem collection sider for at finde produktkortet
      // Start med side 1 og søg fremad indtil vi finder det
      try {
        const product = this.allProducts.find(p => String(p.id) === String(productId));
        if (!product) {
          // console.warn(`⚠️ Produkt ${productId} findes ikke i allProducts`);
          return null;
        }
        
        // Prøv først de første 3 sider (dækker de fleste tilfælde)
        for (let page = 1; page <= 3; page++) {
          const url = `/collections/${this.collectionHandle}?page=${page}`;
          const response = await fetch(url);
          
          if (!response.ok) {
            // console.warn(`❌ HTTP ${response.status} på side ${page}`);
            continue;
          }
          
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          // Find produktkortet i det hentede HTML
          const productCard = doc.querySelector(`[data-product-card][data-product-id="${productId}"]`);
          if (productCard) {
            // console.log(`✅ Fandt produkt ${productId} på side ${page}`);
            
            // Find den omsluttende kolonne
            const column = productCard.closest('.f-column.product-column');
            if (column) {
              return column.outerHTML;
            }
            
            // Fallback: wrap det i en kolonne
            return `<div class="f-column product-column">${productCard.outerHTML}</div>`;
          }
        }
        
        // Hvis ikke fundet i første 3 sider, generer fallback HTML
        // console.warn(`⚠️ Produkt ${productId} ikke fundet i første 3 sider - bruger fallback`);
        return this.generateProductCard(product);
        
      } catch (error) {
        console.error(`❌ Fejl ved hentning af HTML for produkt ${productId}:`, error);
        const product = this.allProducts.find(p => String(p.id) === String(productId));
        if (product) {
          return this.generateProductCard(product);
        }
        return null;
      }
    }
    
    generateProductCard(product) {
      const metafieldsJson = JSON.stringify(
        product.metafields.reduce((acc, mf) => {
          acc[mf.key] = { value: mf.value };
          return acc;
        }, {})
      ).replace(/"/g, '&quot;');
      
      // Pris formattering - product.price er i ØRE
      const priceInKr = product.price / 100;
      const priceFormatted = new Intl.NumberFormat('da-DK', { 
        style: 'currency', 
        currency: 'DKK',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(priceInKr);
      
      // Match temaets product card struktur NØJAGTIGT
      return `
        <div class="f-column product-column">
          <div class="product-card product-card-style-standard color-inherit" 
               data-pcard-version="unified-1" 
               data-product-card 
               data-variant-id="${product.id}"
               data-product-id="${product.id}" 
               data-product-title="${this.escapeHtml(product.title)}" 
               data-variant-price="${priceInKr.toFixed(2)}"
               data-product-price="${product.price}" 
               data-product-metafields="${metafieldsJson}">
            <div class="product-card__wrapper h-full">
              <div class="product-card__image-wrapper product-card__image-wrapper--main-only desktop-color-inverse bg-none">
                <a href="${product.url}" aria-label="${this.escapeHtml(product.title)}" tabindex="-1">
                  <motion-element data-motion="zoom-out-sm" class="block">
                    <div class="media-wrapper product-card__image product-card__image--main" style="--aspect-ratio: 1">
                      ${product.image ? `<img src="${product.image}" alt="${this.escapeHtml(product.title)}" loading="lazy" class="motion-reduce">` : '<svg class="placeholder-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525 525"><path d="M324.553 183.936l22.527 22.527L241.58 312.038v108.127h-47.984V312.038L88.021 206.463l22.527-22.527L241.58 314.975l131.032-131.04h-47.985v-48h144v144h-48V231.95L288.595 363.982l-.014.014z"/></svg>'}
                    </div>
                  </motion-element>
                </a>
              </div>
              <div class="product-card__info text-center">
                <h3 class="product-card__title text-pcard-title">
                  <a class="reversed-link block" href="${product.url}">
                    <span class="reversed-link__text">${this.escapeHtml(product.title)}</span>
                  </a>
                </h3>
                <div class="price text-base">
                  <div class="price__container">
                    <div class="price__regular">
                      <span class="visually-hidden visually-hidden--inline">Normalpris</span>
                      <span class="price-item price-item--regular">${priceFormatted}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    }
    
    hideMissingProductsWarning() {
      const warning = document.getElementById('missing-products-warning');
      if (warning) warning.remove();
    }
    
    hideNoProductsMessage() {
      const message = document.getElementById('no-products-on-page-message');
      if (message) message.remove();
    }
    
    updateProductCount(count) {
      const productCountEl = document.getElementById('ProductCount');
      if (productCountEl) {
        // Vis kun antal matchende produkter - undgå "X af Y" da det kan være misvisende
        productCountEl.textContent = `${count} produkter`;
      }
    }

    shouldSkipMetafield(key) {
      const keyLower = key.toLowerCase();
      
      // Skip bilag
      if (keyLower.includes('bilag_')) return true;
      
      // Skip excluded fields
      if (this.excludedFields.includes(key)) return true;
      
      // Skip single-character or very short fields (fx "ai", "a", etc.)
      if (key.length <= 2) return true;
      
      // Skip fields that look like IDs or technical fields
      if (keyLower.includes('_id') || keyLower.includes('id_')) return true;
      if (keyLower === 'ai' || keyLower === 'id' || keyLower === 'sku') return true;
      
      return false;
    }

    formatLabel(key) {
      let label = key
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      
      return this.applyTextReplacements(label);
    }

    applyTextReplacements(text) {
      let result = text;
      this.replacements.forEach(r => {
        const regex = new RegExp(r.from, 'gi');
        result = result.replace(regex, r.to);
      });
      return result;
    }

    renderFilters() {
      const sortedFilters = Array.from(this.filterData.entries()).sort((a, b) => 
        a[1].label.localeCompare(b[1].label, 'da')
      );

      // Render ALLE filtre først (synlige)
      const filtersHTML = sortedFilters.map(([key, data]) => 
        this.renderFilter(key, data)
      ).join('');
      
      const loadingSpinner = this.container.querySelector('.loading-spinner');
      if (loadingSpinner) loadingSpinner.remove();
      
      this.container.insertAdjacentHTML('beforeend', filtersHTML);

      // Dynamisk beregning af hvor mange filtre der kan være på første række
      // Vent lidt så DOM'en når at rendere og beregne størrelser
      setTimeout(() => this.calculateVisibleFilters(), 100);

      this.attachToggleListeners();
    }
    
    calculateVisibleFilters() {
      const allFilterItems = this.container.querySelectorAll('.smart-filter-item');
      if (allFilterItems.length === 0) return;
      
      const containerWidth = this.container.offsetWidth;
      let currentRowWidth = 0;
      let visibleCount = 0;
      const GAP = 8; // Gap mellem filtre (match CSS)
      const MIN_VISIBLE = 6; // Minimum antal synlige filtre
      
      // console.log(`📐 Container bredde: ${containerWidth}px`);
      
      // Beregn hvor mange filtre der kan være på første række
      for (let i = 0; i < allFilterItems.length; i++) {
        const item = allFilterItems[i];
        const itemWidth = item.offsetWidth;
        
        // console.log(`📏 Filter ${i}: ${itemWidth}px, akkumuleret: ${currentRowWidth + itemWidth}px`);
        
        // Tjek om dette filter stadig passer på rækken ELLER vi ikke har nået minimum
        if (currentRowWidth + itemWidth + GAP <= containerWidth || i < MIN_VISIBLE) {
          currentRowWidth += itemWidth + GAP;
          visibleCount++;
        } else {
          // Rækken er fuld OG vi har nået minimum - stop
          // console.log(`⛔ Rækken fuld ved filter ${i}`);
          break;
        }
      }
      
      // console.log(`✅ Synlige filtre: ${visibleCount} af ${allFilterItems.length}`);
      
      // Skjul resten af filtrene
      const hiddenItems = [];
      allFilterItems.forEach((item, index) => {
        if (index >= visibleCount) {
          item.classList.add('smart-filter-hidden');
          item.style.display = 'none';
          hiddenItems.push(item);
        }
      });
      
      // Tilføj "vis flere" knap hvis der er skjulte filtre
      if (hiddenItems.length > 0) {
        const existingBtn = this.container.querySelector('.smart-filter-show-more');
        if (existingBtn) existingBtn.remove();
        
        const showMoreBtn = document.createElement('button');
        showMoreBtn.type = 'button';
        showMoreBtn.className = 'smart-filter-show-more';
        showMoreBtn.textContent = `+ ${hiddenItems.length} filtre`;
        
        this.container.insertAdjacentElement('beforeend', showMoreBtn);

        showMoreBtn.addEventListener('click', () => {
          const hiddenItems = this.container.querySelectorAll('.smart-filter-hidden');
          const isExpanded = showMoreBtn.classList.contains('expanded');
          
          hiddenItems.forEach(item => {
            item.style.display = isExpanded ? 'none' : '';
          });
          
          showMoreBtn.classList.toggle('expanded');
          
          // Tæl kun SYNLIGE filtre
          const visibleHiddenFilters = Array.from(hiddenItems).filter(item => 
            item.style.display !== 'none'
          );
          const visibleCount = isExpanded ? hiddenItems.length : visibleHiddenFilters.length;
          
          showMoreBtn.textContent = isExpanded
            ? `+ ${hiddenItems.length} filtre` 
            : `- Skjul filtre`;
        });
      }
    }

    renderFilter(key, data, isHidden = false) {
      // Sortér værdier og konverter Map til array med counts
      const values = Array.from(data.values.entries()).sort((a, b) => {
        const [valA] = a;
        const [valB] = b;
        const aNum = parseFloat(valA);
        const bNum = parseFloat(valB);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return aNum - bNum;
        }
        return String(valA).localeCompare(String(valB), 'da');
      });
      
      // SKIP filter hvis der er 0 værdier eller kun én valgmulighed (ingen grund til at filtrere)
      if (values.length <= 1) {
        return '';
      }

      const valuesHtml = values.map(([value, count]) => {
        let displayValue = this.applyTextReplacements(String(value));
        
        if (key.toLowerCase().includes('watt')) {
          const numMatch = displayValue.match(/^(\d+(?:[.,]\d+)?)\s*$/);
          if (numMatch) {
            displayValue = numMatch[1] + ' Watt';
          }
        }
        
        return `
          <label>
            <input type="checkbox" 
                   name="sf_${key}" 
                   value="${this.escapeHtml(value)}">
            <span>${this.escapeHtml(displayValue)}</span>
          </label>
        `;
      }).join('');

      return `
        <div class="smart-filter-item" data-filter="${key}">
          <button type="button" class="smart-filter-toggle">
            ${data.label} <span class="toggle-icon">▼</span>
          </button>
          <div class="smart-filter-dropdown">
            ${valuesHtml}
          </div>
        </div>
      `;
    }

    attachToggleListeners() {
      // Toggle for ALLE filter dropdowns (både statiske og dynamiske)
      const toggles = document.querySelectorAll('.smart-filter-toggle');
      toggles.forEach(toggle => {
        toggle.addEventListener('click', (e) => {
          e.stopPropagation(); // Forhindre at click-outside listener lukker med det samme
          const item = e.currentTarget.closest('.smart-filter-item');
          const dropdown = item.querySelector('.smart-filter-dropdown');
          const icon = toggle.querySelector('.toggle-icon');
          
          const isOpen = dropdown.style.display === 'block';
          
          // Luk ALLE andre dropdowns først
          this.closeAllDropdowns();
          
          // Toggle nuværende dropdown
          dropdown.style.display = isOpen ? 'none' : 'block';
          icon.textContent = isOpen ? '▼' : '▲';
        });
      });
      
      // Luk dropdown når man klikker udenfor
      document.addEventListener('click', (e) => {
        // Hvis klik er IKKE inden i et filter-item, luk alle dropdowns
        if (!e.target.closest('.smart-filter-item')) {
          this.closeAllDropdowns();
        }
      });
    }
    
    closeAllDropdowns() {
      document.querySelectorAll('.smart-filter-dropdown').forEach(dropdown => {
        dropdown.style.display = 'none';
        const icon = dropdown.previousElementSibling?.querySelector('.toggle-icon');
        if (icon) icon.textContent = '▼';
      });
    }
    
    disablePagination(hasActiveFilters) {
      // Find pagination container og load-more button
      const pagination = document.querySelector('.pagination');
      const loadMoreButton = document.querySelector('button[is="load-more-button"]');
      
      if (hasActiveFilters) {
        // Skjul pagination når filtre er aktive
        if (pagination) {
          pagination.style.display = 'none';
        }
        
        // Disable load-more button og fjern event listeners
        if (loadMoreButton) {
          loadMoreButton.style.display = 'none';
          loadMoreButton.disabled = true;
          
          // Fjern type="infinite" så IntersectionObserver ikke trigger
          loadMoreButton.removeAttribute('type');
        }
        
        // console.log('🚫 Pagination disabled - filtre er aktive');
      } else {
        // Genaktiver pagination når alle filtre er ryddet
        if (pagination) {
          pagination.style.display = '';
        }
        
        if (loadMoreButton) {
          loadMoreButton.style.display = '';
          loadMoreButton.disabled = false;
          
          // Gendan type="infinite" hvis det var sat
          const originalType = loadMoreButton.getAttribute('data-original-type');
          if (originalType) {
            loadMoreButton.setAttribute('type', originalType);
          }
        }
        
        // console.log('✅ Pagination enabled - ingen filtre aktive');
      }
    }
    
    savePaginationType() {
      // Gem original type attribute fra load-more button
      const loadMoreButton = document.querySelector('button[is="load-more-button"]');
      if (loadMoreButton && loadMoreButton.hasAttribute('type')) {
        const originalType = loadMoreButton.getAttribute('type');
        loadMoreButton.setAttribute('data-original-type', originalType);
        // console.log(`💾 Gemte pagination type: ${originalType}`);
      }
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
    
    unescapeHtml(html) {
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent;
    }

    showError() {
      this.container.innerHTML = `
        <div class="smart-filters-error">
          <p>Kunne ikke indlæse filtre. Prøv at genindlæse siden.</p>
        </div>
      `;
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    const container = document.getElementById('odoo-filters-container');
    if (container) {
      const loader = new FilterLoader(container);
      loader.init();
    }
  });
})();
